import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MessageBox from '../../components/MessageBox';
import { Skeleton } from '../../components/ui/skeleton';
import faqService from '../../services/faq.service';
import settingsService from '../../services/settings.service';

/* ── Loading skeleton ───────────────────────────────────────────────────────── */
function FaqSkeleton() {
  return (
    <div className='space-y-3'>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className='flex items-center justify-between rounded-xl border border-zinc-200 bg-white px-5 py-4 shadow-sm'
        >
          <Skeleton className={`h-5 ${i % 3 === 0 ? 'w-3/4' : i % 3 === 1 ? 'w-2/3' : 'w-4/5'}`} />
          <Skeleton className='ml-4 h-5 w-5 shrink-0 rounded-full' />
        </div>
      ))}
    </div>
  );
}

/* ── Single accordion item ──────────────────────────────────────────────────── */
function FaqItem({ question, answer, isOpen, onToggle }) {
  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white shadow-sm transition-colors ${
        isOpen ? 'border-zinc-300' : 'border-zinc-200 hover:border-zinc-300'
      }`}
    >
      <button
        type='button'
        onClick={onToggle}
        style={{ backgroundColor: 'transparent' }}
        className='flex w-full items-center justify-between gap-4 px-5 py-4 text-left focus:outline-none'
      >
        <span className='text-sm font-semibold leading-snug text-ink lg:text-base'>{question}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-zinc-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className='border-t border-zinc-100 bg-zinc-50/50 px-5 py-4 text-sm leading-relaxed text-zinc-600'>
          {answer}
        </div>
      )}
    </div>
  );
}

/* ── Error-report modal ─────────────────────────────────────────────────────── */
function ReportModal({ iframeSrc, onClose }) {
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4'
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className='relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl'>
        <div className='flex items-center justify-between border-b border-divider px-5 py-4'>
          <h2 className='text-base font-semibold text-ink'>Reportar error</h2>
          <button
            type='button'
            onClick={onClose}
            style={{ backgroundColor: 'transparent' }}
            className='flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 focus:outline-none'
            aria-label='Cerrar'
          >
            <X className='h-4 w-4' />
          </button>
        </div>
        <div className='h-[420px]'>
          {iframeSrc ? (
            <iframe src={iframeSrc} title='Reportar error' className='h-full w-full border-0' />
          ) : (
            <div className='flex h-full items-center justify-center text-sm text-zinc-400'>
              No hay formulario configurado.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────────────────── */
export default function FaqPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [iframeSrc, setIframeSrc] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [faqData, settingsData] = await Promise.all([
          faqService.getFaqs(),
          settingsService.getPublicSettings(),
        ]);
        setQuestions(faqData || []);
        setIframeSrc(settingsData?.iframeUrl || '');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const toggle = index => setOpenIndex(prev => (prev === index ? null : index));

  return (
    <div className='min-h-screen bg-white font-sans text-ink'>
      <Helmet>
        <title>{t('nav.faq')}</title>
      </Helmet>

      <div className='mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-14'>
        <div className='lg:grid lg:grid-cols-5 lg:gap-16'>
          {/* ── Left column: sticky header + report button ─────────── */}
          <div className='lg:col-span-2 lg:sticky lg:top-24 lg:self-start'>
            <h1 className='text-2xl font-bold leading-tight sm:text-3xl'>
              {t('faq.howCanWeHelp')}
            </h1>
            <p className='mt-3 text-sm leading-relaxed text-zinc-500'>
              Encontrá respuestas a las preguntas más frecuentes sobre nuestros bonos.
            </p>
            <button
              type='button'
              onClick={() => setShowModal(true)}
              style={{ backgroundColor: 'transparent' }}
              className='mt-6 rounded-xl border border-red-400 px-4 py-2 text-sm font-medium text-red-500 transition-colors hover:bg-red-50 focus:outline-none'
            >
              {t('faq.reportError')}
            </button>
          </div>

          {/* ── Right column: FAQ accordion ─────────────────────────── */}
          <div className='mt-8 lg:col-span-3 lg:mt-0'>
            {loading ? (
              <FaqSkeleton />
            ) : error ? (
              <MessageBox variant='danger'>{error}</MessageBox>
            ) : questions.length === 0 ? (
              <p className='text-sm text-zinc-400'>No hay preguntas frecuentes por el momento.</p>
            ) : (
              <div className='space-y-3'>
                {questions.map(({ question, answer }, index) => (
                  <FaqItem
                    key={index}
                    question={question}
                    answer={answer}
                    isOpen={openIndex === index}
                    onToggle={() => toggle(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Error report modal ────────────────────────────────────── */}
      {showModal && <ReportModal iframeSrc={iframeSrc} onClose={() => setShowModal(false)} />}
    </div>
  );
}
